import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import FiveBanner from '../components/FiveBanner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Registration = { id: number; serialNumber: string; status: string; warrantyEndDate: string | null; product: { name: string } | null };

function statusBadge(status: string) {
  const map: Record<string, string> = {
    active:      'bg-green-100 text-green-700',
    expired:     'bg-red-100 text-red-700',
    voided:      'bg-gray-100 text-gray-500',
    transferred: 'bg-blue-100 text-blue-700',
  };
  return `badge ${map[status] ?? 'bg-gray-100 text-gray-500'}`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [regs, setRegs]   = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);

  useEffect(() => {
    api.get<Registration[]>('/registrations').then(r => {
      setRegs(r);
      setLoading(false);
    }).catch(() => setLoading(false));

    api.get('/companies/my').then((c: any) => setHasCompany(!!c)).catch(() => setHasCompany(false));
  }, []);

  const active      = regs.filter(r => r.status === 'active').length;
  const expiringSoon = regs.filter(r => {
    if (!r.warrantyEndDate || r.status !== 'active') return false;
    const days = (new Date(r.warrantyEndDate).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 60;
  }).length;

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboard.welcomeBack')}{user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <p className="text-gray-500 text-sm mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[
          { label: t('dashboard.activeRegistrations'), value: active, color: 'text-green-600' },
          { label: t('dashboard.expiringDays'), value: expiringSoon, color: 'text-amber-600' },
          { label: t('dashboard.totalProducts'), value: regs.length, color: 'text-brand-red' },
        ].map(s => (
          <div key={s.label} className="card text-center">
            <p className={`text-3xl font-bold ${s.color}`}>{loading ? '–' : s.value}</p>
            <p className="text-sm text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Recent registrations */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{t('dashboard.recentProducts')}</h2>
          <Link to="/registrations/new" className="btn-primary text-xs px-3 py-1.5">{t('dashboard.registerProduct')}</Link>
        </div>
        {loading ? (
          <p className="text-sm text-gray-400">{t('common.loading')}</p>
        ) : regs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 text-sm mb-3">{t('dashboard.noProducts')}</p>
            <Link to="/registrations/new" className="btn-primary">{t('dashboard.registerFirst')}</Link>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full text-sm" style={{ minWidth: 500 }}>
              <thead><tr className="border-b border-gray-100 text-left text-gray-500 text-xs uppercase tracking-wide">
                <th className="pb-2">{t('dashboard.product')}</th>
                <th className="pb-2">{t('dashboard.serial')}</th>
                <th className="pb-2">{t('dashboard.warrantyEnds')}</th>
                <th className="pb-2">{t('dashboard.status')}</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {regs.slice(0, 5).map(r => (
                  <tr key={r.id}>
                    <td className="py-2.5 font-medium">{r.product?.name ?? '—'}</td>
                    <td className="py-2.5 text-gray-500 font-mono">{r.serialNumber}</td>
                    <td className="py-2.5 text-gray-500">{r.warrantyEndDate ?? '—'}</td>
                    <td className="py-2.5"><span className={statusBadge(r.status)}>{t(`status.${r.status}`)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {regs.length > 5 && (
          <Link to="/registrations" className="block text-center text-sm text-brand-red mt-4 hover:underline">
            {t('dashboard.viewAll', { count: regs.length })}
          </Link>
        )}
      </div>

      {/* Company setup prompt */}
      {hasCompany === false && (
        <div className="mt-6 flex items-center justify-between gap-4 px-5 py-4 rounded-lg" style={{ background: '#ffffff', border: '1px solid #e5e5e5' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: '#f4f4f4' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8f9b9b" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#0b0d0e' }}>{t('dashboard.companySetupTitle')}</p>
              <p className="text-xs" style={{ color: '#8f9b9b' }}>{t('dashboard.companySetupDesc')}</p>
            </div>
          </div>
          <Link to="/settings?tab=company" className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap">{t('dashboard.companySetupCta')}</Link>
        </div>
      )}

      {/* FIVE banner */}
      <div className="mt-6">
        <FiveBanner />
      </div>

      {/* Sustainability banner */}
      <a
        href="https://montipower.com/pages/sustainability"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 flex items-stretch rounded-lg overflow-hidden no-underline group"
        style={{ border: '1px solid #e5e5e5' }}
      >
        {/* Red accent stripe */}
        <div style={{ width: 6, background: '#e00000', flexShrink: 0 }} />

        <div className="flex-1 flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-5" style={{ background: '#ffffff' }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#e00000' }}>
              {t('dashboard.sustainabilityTag')}
            </p>
            <p className="font-semibold text-sm mb-1" style={{ color: '#0b0d0e' }}>
              {t('dashboard.sustainabilityHeadline')}
            </p>
            <p className="text-xs" style={{ color: '#8f9b9b', maxWidth: 480 }}>
              {t('dashboard.sustainabilityBody')}
            </p>
          </div>
          <div className="flex items-center gap-5 sm:gap-4 sm:flex-shrink-0">
            {[
              { value: '~155 MWh', label: t('dashboard.solar') },
              { value: '25%',      label: t('dashboard.fleet')  },
              { value: 'ISO 9001', label: t('dashboard.iso') },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="font-bold text-sm" style={{ color: '#0b0d0e' }}>{s.value}</p>
                <p style={{ fontSize: 10, color: '#8f9b9b' }}>{s.label}</p>
              </div>
            ))}
            <div style={{ color: '#e00000', fontSize: 18, flexShrink: 0 }}>›</div>
          </div>
        </div>
      </a>
    </Layout>
  );
}
